import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { canUseApplePay } from '../availability';
import { isGooglePayAvailable } from './googlePay';
import type { Location } from '@/api/types';
import type { WalletCapabilities } from '../availability';

/**
 * What this device can actually present, asked properly.
 *
 * Apple's answer is synchronous — `ApplePaySession.canMakePayments()` is a function call. Google's is not: it
 * needs their SDK fetched and a round trip into the guest's own Google account. So the pair is resolved
 * asynchronously and handed to `availablePaymentMethods`, which stays a pure synchronous function taking
 * capabilities as an argument. That separation is why making the Google half real did not ripple through
 * every screen that asks what a guest can pay with.
 *
 * Cached per location because it is asked twice: once by the tip step, which needs it to decide whether the
 * method screen is worth showing, and again by the method screen itself.
 */
export function walletCapabilitiesQuery(location: Location) {
    return {
        queryKey: ['walletCapabilities', location.id],
        queryFn: async (): Promise<WalletCapabilities> => ({
            applePay: canUseApplePay(),
            googlePay: await isGooglePayAvailable(location),
        }),
        // A device does not sprout a wallet mid-checkout, and the SDK fetch is not worth repeating.
        staleTime: Infinity,
        retry: false,
    };
}

/**
 * Starts the probe and reports where it got to.
 *
 * Called from the tip step so the answer is ready by the time a guest finishes choosing one — the question
 * takes a network round trip, and asking it at the moment they tap Pay would put that delay in front of them.
 */
export function useWalletCapabilities(location: Location) {
    return useQuery(walletCapabilitiesQuery(location));
}

/**
 * The settled answer, waiting for the probe if it is still running.
 *
 * For the moment a decision genuinely cannot be deferred: the tip step routing to the method screen or past
 * it. Resolves instantly from cache in the normal case, because the probe was started when the step mounted.
 *
 * A probe that fails answers "no wallets" rather than throwing. Card always works, so the worst outcome is a
 * guest paying by card on a phone that could have managed a wallet — which beats a checkout that stops.
 */
export async function resolveWalletCapabilities(
    queryClient: QueryClient,
    location: Location,
): Promise<WalletCapabilities> {
    try {
        return await queryClient.fetchQuery(walletCapabilitiesQuery(location));
    } catch {
        return { applePay: false, googlePay: false };
    }
}

export { useQueryClient };
